import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useAuthStore } from '../stores/auth-store';
import type { UserRole } from '../types';

export function useOtpRequest() {
  return useMutation({
    mutationFn: (data: { email: string; clientCode: string }) =>
      api.post<{ message: string }>('/auth/otp/request', data),
  });
}

export function useOtpVerify() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string; code: string }) =>
      api.post<{ user: { id: string; email: string; name: string; role: UserRole; clientElementId?: number } }>(
        '/auth/otp/verify',
        data,
      ),
    onSuccess: ({ data }) => {
      setAuth(data.user);
      // Invalidate all cached queries so fresh data loads after login
      qc.invalidateQueries();
    },
  });
}

export function useMicrosoftLoginUrl() {
  return useMutation({
    mutationFn: () => api.get<{ url: string }>('/auth/microsoft'),
  });
}

export function useMicrosoftCodeExchange() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (data: { code: string }) =>
      api.post<{ user: { id: string; email: string; name: string; role: UserRole; clientElementId?: number } }>(
        '/auth/microsoft/exchange',
        data,
      ),
    onSuccess: ({ data }) => {
      setAuth(data.user);
      qc.invalidateQueries();
    },
  });
}

export function useLogoutAction() {
  const logout = useAuthStore((s) => s.logout);

  return async () => {
    try {
      await api.post<{ loggedOut: boolean }>('/auth/logout');
    } catch {
      // Ignore request errors and always clear local auth state.
    } finally {
      logout();
    }
  };
}
